/**
 * `MermaidBlock` — the Tiptap node that turns a ```mermaid fence into an interactive diagram
 * block (interactive-diagrams-v0.1.md §5.2).
 *
 * It is an **atom** node whose only state is a `source` string (the full fence body, including
 * any `%% @aletheia` sidecar). The on-screen rendering is delegated to a React NodeView
 * (`MermaidBlockView`); the *serialization* DOM is a plain `<pre><code class="language-mermaid">`
 * so `editor.getHTML()` → turndown reproduces the exact fence — the markdown round-trip stays
 * byte-stable, and any other renderer sees ordinary mermaid.
 *
 * Routing: `parseHTML` matches a `<pre>` containing a `code.language-mermaid` at higher priority
 * than `CodeBlockLowlight`, and returns `false` for every other `<pre>` so normal code blocks
 * fall through to lowlight untouched.
 */

import { Node } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { MermaidBlockView } from './MermaidBlockView';

export const MermaidBlock = Node.create({
  name: 'mermaidBlock',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: false,
  // A whole diagram is one unit — no partial selection/merge with siblings.
  isolating: true,

  addAttributes() {
    return {
      source: {
        default: '',
        // Source is emitted as the <code> text content in renderHTML, not as an attribute.
        renderHTML: () => ({}),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'pre',
        // Beat CodeBlockLowlight's default-priority `pre` rule.
        priority: 100,
        getAttrs: (dom) => {
          const el = dom as HTMLElement;
          const code = el.querySelector('code');
          if (!code) return false;
          const cls = code.getAttribute('class') ?? '';
          if (!/\blanguage-mermaid\b/.test(cls)) return false; // not mermaid → let lowlight handle it
          // Strip a single trailing newline that `marked` leaves inside the code element.
          const source = (code.textContent ?? '').replace(/\n$/, '');
          return { source };
        },
      },
    ];
  },

  renderHTML({ node }) {
    const source = typeof node.attrs.source === 'string' ? node.attrs.source : '';
    // `<pre><code class="language-mermaid">…</code></pre>` → turndown emits ```mermaid … ```.
    return ['pre', ['code', { class: 'language-mermaid' }, source]];
  },

  addNodeView() {
    return ReactNodeViewRenderer(MermaidBlockView);
  },
});
